// Onboarding WIZARD step model (guided onboarding, Phase 2).
//
// The wizard's first 5 steps -- business profile, plan, credits, phone,
// A2P registration -- are the SAME for every subscriber regardless of
// insurance line: every agency needs a phone number and A2P approval to
// text. This module defines what comes AFTER those base steps: the
// line-specific "launch sequence" the subscriber sees once they've picked
// their insurance line(s) in Step 1 (see InsuranceLinesStep / the
// insurance_lines model in ./insuranceLines.js).
//
// Each extra step routes to an EXISTING, already-built view (LicensingView,
// client import, comp setup, HealthSherpa import, marketplace consent,
// SOA) -- this module never invents a new UI, it only decides the ORDER
// and WHICH steps show for a given line selection. Kept pure and
// dependency-free (besides insuranceLines) so the mapping is
// unit-testable, following the same pattern as api/_activationSteps.js
// (the line-specific activation checklist this wizard hands off to).
//
// Anti-fabrication: this module is pure ordering/routing metadata. It
// never claims a step is "done" -- real done-state for these same
// destinations already lives in api/_activationSteps.js. The wizard does
// not duplicate that signal here; it is a guided tour, not a checklist.

'use strict';

const { normalizeLines, hasLine } = require('./insuranceLines');

const LIFE_WIZARD_STEPS = [
  {
    key: 'life_licensing',
    title: 'Licenses & carrier appointments',
    short: 'Licensing',
    body: 'Add your state license(s) and the carriers you are appointed with, including your writing number for each.',
    cta_tab: 'licensing',
    cta_label: 'Open Licensing',
  },
  {
    key: 'life_book',
    title: 'Import your book or upload a statement',
    short: 'Book',
    body: 'Bring in your existing clients, or upload a carrier commission statement so AI can reconcile pay against your book automatically.',
    cta_tab: 'clients',
    cta_label: 'Import clients',
    secondary_cta_tab: 'life_commissions',
    secondary_cta_label: 'Upload a statement',
  },
  {
    key: 'life_comp',
    title: 'Set your compensation levels',
    short: 'Comp',
    body: 'Set your contract level and comp % per carrier so commission math matches what you actually get paid.',
    cta_tab: 'comp_setup',
    cta_label: 'Configure comp levels',
  },
];

const ACA_WIZARD_STEPS = [
  {
    key: 'aca_consent_import',
    title: 'Marketplace consent & book import',
    short: 'Consent & Book',
    body: 'Get your marketplace consent flow ready, then import your HealthSherpa book so OEP renewals and subsidy tracking run automatically.',
    cta_tab: 'health_consents',
    cta_label: 'Set up consent',
    secondary_cta_tab: 'health_policies',
    secondary_cta_label: 'Import ACA book',
  },
];

const MEDICARE_WIZARD_STEPS = [
  {
    key: 'medicare_soa_import',
    title: 'SOA setup & book import',
    short: 'SOA & Book',
    body: 'Set up your Scope of Appointment, then import your Medicare book so AEP and birthday-rule renewal tracking run automatically.',
    cta_tab: 'soa',
    cta_label: 'Open SOA',
    secondary_cta_tab: 'health_policies',
    secondary_cta_label: 'Import Medicare book',
  },
];

// Build the ordered, line-aware list of extra wizard steps. `lines` is the
// subscriber's selected insurance lines (normalized defensively). Order is
// canonical (life, then aca, then medicare) regardless of selection order,
// matching api/_activationSteps.js so both surfaces agree on sequence.
// private_health alone maps to no extra steps -- honest "nothing to add
// yet" rather than a fabricated launch sequence (same stance the
// activation checklist takes).
function buildWizardLineSteps(lines) {
  const safe = normalizeLines(lines);
  const steps = [];
  if (hasLine(safe, 'life')) steps.push(...LIFE_WIZARD_STEPS);
  if (hasLine(safe, 'aca')) steps.push(...ACA_WIZARD_STEPS);
  if (hasLine(safe, 'medicare')) steps.push(...MEDICARE_WIZARD_STEPS);
  return steps;
}

module.exports = {
  LIFE_WIZARD_STEPS,
  ACA_WIZARD_STEPS,
  MEDICARE_WIZARD_STEPS,
  buildWizardLineSteps,
};
